import { setDocumentFromRequest } from "@/models/Document";
import { setNoteFromRequest } from "@/models/Note";
import { uuidv4, getRouterLink } from "@/util/helpers";
import { Exam, Task, PhysicianStatement } from "@/models/Task";
import { setInformalOfferFromRequest } from "@/models/InformalOffer";
import { setRoleFromRequest } from "@/models/Role";
import { setAddressFromRaw } from "@/models/Address";

export const ELIMINATION_PERIODS = [
  { title: "0 Days", value: "0 Days" },
  { title: "30 Days", value: "30 Days" },
  { title: "60 Days", value: "60 Days" },
  { title: "90 Days (recommended)", value: "90 Days" },
  { title: "180 Days", value: "180 Days" },
  { title: "1 Year", value: "1 Year" },
  { title: "2 Years", value: "2 Years" },
  { title: "5 Years", value: "5 Years" }
];

export const CASE_STATUS = {
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  AWAITING_REISSUED_POLICY: "Awaiting Reissued Policy",
  AWAITING_RECONSIDERATION: "Awaiting Reconsideration",
  ISSUED: "Issued",
  AWAITING_DELIVERY_REQUIREMENTS: "Awaiting Delivery Requirements",
  RECEIVED_DELIVERY_REQUIREMENTS: "Received Delivery Requirements",
  ACTIVE: "Active",
  ACTIVE_PENDING_REQUIREMENTS: "Active - Pending Requirements"
};

export const STATUSES = [
  "Entered",
  CASE_STATUS.SUBMITTED,
  CASE_STATUS.APPROVED,
  CASE_STATUS.ISSUED,
  CASE_STATUS.AWAITING_REISSUED_POLICY,
  CASE_STATUS.AWAITING_RECONSIDERATION,
  CASE_STATUS.AWAITING_DELIVERY_REQUIREMENTS,
  CASE_STATUS.RECEIVED_DELIVERY_REQUIREMENTS,
  CASE_STATUS.ACTIVE_PENDING_REQUIREMENTS,
  CASE_STATUS.ACTIVE,
  "Withdrawn",
  "Not Taken",
  "Postponed",
  "Declined",
  "Closed - Not Submitted to Carrier",
  "Closed - Submitted by Another GA",
  "Grace Period",
  "Lapsed",
  "Deceased/Death Claim"
];

export const INFORMAL_STATUSES = [
  "All Offers In",
  "Informal Formalized",
  "Informal Closed",
  "Awaiting Carrier Offers",
  "Gathering Medical Information",
  "Informal Entered"
];

export const MODE_OPTIONS = [
  { title: "Monthly", value: 12 },
  { title: "Quarterly", value: 4 },
  { title: "Semi-Annual", value: 2 },
  { title: "Annually", value: 1 }
];

export const TRACKING_TYPES = [{ title: "USPS" }, { title: "FedEx" }];

export const RATINGS_BY_LINE = {
  annuity: [
    "Super Preferred",
    "Preferred NT",
    "Preferred Tobacco",
    "Standard Plus",
    "Standard NT",
    "Standard Tobacco",
    "Table 1 Tobacco",
    "Table 2 Tobacco",
    "Table 3 Tobacco",
    "Table 4 Tobacco",
    "Table 5 Tobacco",
    "Table 6 Tobacco",
    "Table 7 Tobacco",
    "Table 8 Tobacco",
    "Table 9 Tobacco",
    "Table 10 Tobacco",
    "Table 1",
    "Table 2",
    "Table 3",
    "Table 4",
    "Table 5",
    "Table 6",
    "Table 7",
    "Table 8",
    "Table 9",
    "Table 10",
    "Table 11",
    "Table 12",
    "Table 13",
    "Table 14",
    "Table 15",
    "Table 16",
    "Table 17",
    "Table 18",
    "Table 19",
    "Table 20",
    "Declined",
    "Postponed"
  ],
  disability: [
    "Super Preferred",
    "Preferred NT",
    "Preferred Tobacco",
    "Standard Plus",
    "Standard NT",
    "Standard Tobacco",
    "Table 1 Tobacco",
    "Table 2 Tobacco",
    "Table 3 Tobacco",
    "Table 4 Tobacco",
    "Table 5 Tobacco",
    "Table 6 Tobacco",
    "Table 7 Tobacco",
    "Table 8 Tobacco",
    "Table 9 Tobacco",
    "Table 10 Tobacco",
    "Table 1",
    "Table 2",
    "Table 3",
    "Table 4",
    "Table 5",
    "Table 6",
    "Table 7",
    "Table 8",
    "Table 9",
    "Table 10",
    "Table 11",
    "Table 12",
    "Table 13",
    "Table 14",
    "Table 15",
    "Table 16",
    "Table 17",
    "Table 18",
    "Table 19",
    "Table 20",
    "Declined",
    "Postponed"
  ],
  life: [
    "Super Preferred",
    "Preferred NT",
    "Preferred Tobacco",
    "Standard Plus",
    "Standard NT",
    "Standard Tobacco",
    "Table 1",
    "Table 1 Tobacco",
    "Table 2",
    "Table 2 Tobacco",
    "Table 3",
    "Table 3 Tobacco",
    "Table 4",
    "Table 4 Tobacco",
    "Table 5",
    "Table 5 Tobacco",
    "Table 6",
    "Table 6 Tobacco",
    "Table 7",
    "Table 7 Tobacco",
    "Table 8",
    "Table 8 Tobacco",
    "Table 9",
    "Table 9 Tobacco",
    "Table 10",
    "Table 10 Tobacco",
    "Table 11",
    "Table 12",
    "Table 13",
    "Table 14",
    "Table 15",
    "Table 16",
    "Table 17",
    "Table 18",
    "Table 19",
    "Table 20",
    "Level",
    "Level Tobacco",
    "Graded",
    "Guaranteed Issue",
    "Declined",
    "Postponed"
  ],
  linked_benefit: [
    "Super Preferred",
    "Preferred NT",
    "Preferred Tobacco",
    "Standard Plus",
    "Standard NT",
    "Standard Tobacco",
    "Table 1 Tobacco",
    "Table 2 Tobacco",
    "Table 3 Tobacco",
    "Table 4 Tobacco",
    "Table 5 Tobacco",
    "Table 6 Tobacco",
    "Table 7 Tobacco",
    "Table 8 Tobacco",
    "Table 9 Tobacco",
    "Table 10 Tobacco",
    "Table 1",
    "Table 2",
    "Table 3",
    "Table 4",
    "Table 5",
    "Table 6",
    "Table 7",
    "Table 8",
    "Table 9",
    "Table 10",
    "Table 11",
    "Table 12",
    "Table 13",
    "Table 14",
    "Table 15",
    "Table 16",
    "Table 17",
    "Table 18",
    "Table 19",
    "Table 20",
    "Declined",
    "Postponed"
  ],
  ltc: ["Preferred", "Select", "Class 1", "Class 2", "Postponed", "Declined"],
  variable_life: [
    "Super Preferred",
    "Preferred NT",
    "Preferred Tobacco",
    "Standard Plus",
    "Standard NT",
    "Standard Tobacco",
    "Table 1 Tobacco",
    "Table 2 Tobacco",
    "Table 3 Tobacco",
    "Table 4 Tobacco",
    "Table 5 Tobacco",
    "Table 6 Tobacco",
    "Table 7 Tobacco",
    "Table 8 Tobacco",
    "Table 9 Tobacco",
    "Table 10 Tobacco",
    "Table 1",
    "Table 2",
    "Table 3",
    "Table 4",
    "Table 5",
    "Table 6",
    "Table 7",
    "Table 8",
    "Table 9",
    "Table 10",
    "Table 11",
    "Table 12",
    "Table 13",
    "Table 14",
    "Table 15",
    "Table 16",
    "Table 17",
    "Table 18",
    "Table 19",
    "Table 20",
    "Declined",
    "Postponed"
  ]
};

export const RATINGS_FOR_LINES = lines => {
  const set = new Set();
  lines.forEach(line => {
    RATINGS_BY_LINE[line].forEach(rating => set.add(rating));
  });

  return Array.from(set);
};

export const TAX_OPTION = {
  _401K: "401k",
  _403B: "403b",
  _412I_PLAN: "412i Plan",
  _457_DEFERRED_COMPENSATION: "457 Deferred Compensation",
  INHERITED_IRA_PLAN: "Inherited IRA Plan",
  INHERITED_ROTH_IRA_PLAN: "Inherited Roth IRA Plan",
  IRA: "IRA",
  NON_QUALIFIED: "Non-Qualified",
  PENSION_PLAN: "Pension Plan",
  PROFIT_SHARING_PLAN: "Profit Sharing Plan",
  ROTH_401K: "Roth 401k",
  ROTH_403B: "Roth 403b",
  ROTH_IRA: "Roth IRA",
  SEP_IRA: "SEP-IRA",
  SIMPLE_IRA: "SIMPLE IRA"
};

export const TAX_OPTIONS = Object.values(TAX_OPTION);

export const OCCUPATION_CLASSES = [
  "6A",
  "6S",
  "5A",
  "5M",
  "4S",
  "4P",
  "4A",
  "4M",
  "3A",
  "3P",
  "3AM",
  "2A",
  "2L",
  "1L",
  "A",
  "B"
];

export const COLA_OPTIONS = [
  { title: "None", value: null },
  { title: "1% Compound", value: "1% Compound" },
  { title: "2% Compound", value: "2% Compound" },
  { title: "3% Compound", value: "3% Compound" },
  { title: "4% Compound", value: "4% Compound" },
  { title: "5% Compound", value: "5% Compound" },
  { title: "6% Compound", value: "6% Compound" }
];

export const CASE_STATUSES = [
  "Entered",
  "Submitted",
  "Approved",
  "Issued",
  "Awaiting Reissued Policy",
  "Awaiting Reconsideration",
  "Awaiting Delivery Requirements",
  "Received Delivery Requirements",
  "Active - Pending Requirements",
  "Active",
  "Withdrawn",
  "Not Taken",
  "Postponed",
  "Declined",
  "Closed - Not Submitted to Carrier",
  "Closed - Submitted by Another GA",
  "Grace Period",
  "Lapsed",
  "Deceased/Death Claim"
];

export const DISABILITY_BENEFIT_PERIODS = [
  "Lump Sum",
  "6 Months",
  "12 Months",
  "18 Months",
  "2 Years",
  "3 Years",
  "4 Years",
  "5 Years",
  "10 Years",
  "To Age 65",
  "To Age 66",
  "To Age 67",
  "To Age 70"
];

export const BILLING_PLANS = ["Direct Bill", "EFT", "List Bill"];
export const MODES = [
  { title: "Monthly", value: 12 },
  { title: "Quarterly", value: 4 },
  { title: "Semi-Annually", value: 2 },
  { title: "Annually", value: 1 }
];

export const BENEFIT_MODES = ["Daily", "Monthly"];

export const CASE_LINES = [
  { title: "Annuity", value: "annuity" },
  { title: "Disability", value: "disability" },
  { title: "Life", value: "life" },
  { title: "LTC", value: "ltc" },
  { title: "Linked Benefit", value: "linked_benefit" },
  { title: "Informal", value: "informal" },
  { title: "Variable Life", value: "variable_life" }
];

export const STATUS_FILTER_OPTIONS = [
  { title: "Pending", value: "pending" },
  { title: "Active", value: "active" },
  { title: "Inactive", value: "inactive" }
];

export const PENDING_STATUSES = [
  "Entered",
  "Submitted",
  "Approved",
  "Issued",
  "Awaiting Reissued Policy",
  "Awaiting Reconsideration",
  "Awaiting Delivery Requirements",
  "Received Delivery Requirements",
  "Informal Entered",
  "All Offers In",
  "Gathering Medical Information",
  "Awaiting Carrier Offers"
];

export const ACTIVE_STATUSES = [
  "Active - Pending Requirements",
  "Active",
  "Informal Formalized"
];

export const INACTIVE_STATUSES = [
  "Withdrawn",
  "Not Taken",
  "Postponed",
  "Declined",
  "Closed - Not Submitted to Carrier",
  "Closed - Submitted by Another GA",
  "Grace Period",
  "Lapsed",
  "Deceased/Death Claim",
  "Informal Closed"
];

export const ALL_STATUSES = [
  { header: "Pending" },
  ...PENDING_STATUSES,
  { header: "Active" },
  ...ACTIVE_STATUSES,
  { header: "Inactive" },
  ...INACTIVE_STATUSES
];

export const statusFilterGroups = [
  { title: "Pending", value: PENDING_STATUSES },
  { title: "Inactive", value: INACTIVE_STATUSES },
  { title: "Active", value: ACTIVE_STATUSES }
];

export const LINES = [
  { title: "Annuity", value: "annuity" },
  { title: "Disability", value: "disability" },
  { title: "Linked Benefit", value: "linked_benefit" },
  { title: "Life", value: "life" },
  { title: "LTC", value: "ltc" },
  { title: "Variable Life", value: "variable_life" }
];

export function Case(model = {}) {
  return {
    address: model?.address || null,
    addresses: model?.addresses || [],
    advisors: model?.advisors || [],
    appliedMode: model?.appliedMode || null,
    appliedPremium: model?.appliedPremium || null,
    appliedPremiumTotal: model?.appliedPremiumTotal || null,
    appointmentCases: model?.appointmentCases || [],
    approvedPremium: model?.approvedPremium || 0,
    approvedPremiumTotal: model?.approvedPremiumTotal || 0,
    assignOptions: model?.assignOptions || [],
    benefitAmount: model?.benefitAmount || null,
    benefitMode: model?.benefitMode || null,
    benefitPeriod: model?.benefitPeriod || null,
    carrier: model?.carrier || null,
    carrierEDelivery: model?.carrierEDelivery || null,
    caseConcierge: model?.caseConcierge || null,
    caseManager: model?.caseManager || null,
    cashReceived: model?.cashReceived || null,
    childRider: model?.childRider || null,
    chronicIllnessRider: model?.chronicIllnessRider || null,
    commission: model?.commission || null,
    commissionable: model?.commissionable || null,
    contractParties: model?.contractParties || [],
    conversion: model?.conversion,
    conversionEapps: model?.conversionEapps || [],
    conversionExpiry: model?.conversionExpiry || null,
    convertible: model?.convertible,
    daysPending: model?.daysPending || null,
    documents: model?.documents || [],
    electronicApplication: model?.electronicApplication || null,
    eliminationPeriod: model?.eliminationPeriod || null,
    exchangeAmount: model?.exchangeAmount || null,
    faceAmount: model?.faceAmount || null,
    gaCode: model?.gaCode || null,
    id: model?.id || null,
    impairedRiskQuotes: model?.impairedRiskQuotes || [],
    inflation: model?.inflation || null,
    informals: model?.informals || null,
    ltcRider: model?.ltcRider || null,
    lumpSum: model?.lumpSum || null,
    messageClient: model?.messageClient || null,
    mode: model?.mode || null,
    notes: model?.notes || [],
    outstanding: model?.outstanding || null,
    policyNumber: model?.policyNumber || null,
    premium: model?.premium || null,
    product: model?.product || null,
    productionCreditTotal: model?.productionCreditTotal || null,
    products: model?.products || [],
    quotes: model?.quotes || [],
    requirements: model?.requirements || [],
    requireTargetPremium: Boolean(model?.requireTargetPremium),
    room: model?.room || [],
    sameManagerForParty: Boolean(model?.sameManagerForParty),
    saveAge: Boolean(model?.saveAge),
    showApprovedPremium: Boolean(model?.showApprovedPremium),
    showSnapshot: Boolean(model?.showSnapshot),
    state: model?.state || null,
    status: model?.status || null,
    surrenderChargeExpiration: model?.surrenderChargeExpiration || null,
    targetPremium: model?.targetPremium || null,
    taxType: model?.taxType || null,
    temporaryInsurance: Boolean(model?.temporaryInsurance),
    termRider: Boolean(model?.termRider),
    tia: model?.tia || null,
    timeline: model?.timeline || [],
    totalPremium: model?.totalPremium || null,
    trackingNumber: model?.trackingNumber || null,
    trackingNumberLink: model?.trackingNumberLink || null,
    trackingNumberType: model?.trackingNumberType || null,
    transactions: model?.transactions || [],
    updatedAt: model?.updatedAt || null
  };
}

export function setCaseFromRequest(raw = {}) {
  const model = Case();

  model.appliedMode = raw?.applied_mode;
  model.appliedPremium = raw?.applied_premium?.premium;
  model.appliedPremiumTotal = raw?.applied_premium?.total;
  model.approvedPremium = raw?.premium?.premium;
  model.approvedPremiumTotal = raw?.premium?.total;
  model.benefitAmount = raw?.benefit_amount;
  model.benefitMode = raw?.benefit_mode;
  model.benefitPeriod = raw?.benefit_period;
  model.carrierEDelivery = raw?.carrier_e_delivery;
  model.cashReceived = raw?.cash_received;
  model.childRider = raw?.child_rider;
  model.chronicIllnessRider = raw?.chronic_illness_rider;
  model.commission = raw?.commission;
  model.commissionable = raw?.commissionable;
  model.conversion = raw?.conversion;
  model.conversionExpiry = raw?.conversion_expiry;
  model.convertible = Boolean(raw?.convertible);
  model.daysPending = raw?.days_pending;
  model.electronicApplication = raw?.electronic_application;
  model.eliminationPeriod = raw?.elimination_period;
  model.exchangeAmount = raw?.exchange_amount;
  model.faceAmount = raw?.face_amount;
  model.gaCode = raw?.ga_code;
  model.id = raw?.id;
  model.inflation = raw?.inflation;
  model.ltcRider = raw?.ltc_rider;
  model.lumpSum = raw?.lump_sum;
  model.messageClient = raw?.message_client;
  model.mode = raw?.mode;
  model.outstanding = raw?.outstanding;
  model.policyNumber = raw?.policy_number;
  model.premium = raw?.premium?.premium;
  model.productionCreditTotal = raw?.production_credit_total;
  model.sameManagerForParty = raw?.same_manager_for_party;
  model.saveAge = raw?.save_age;
  model.showSnapshot = raw?.show_snapshot;
  model.state = raw?.state;
  model.status = raw?.status;
  model.surrenderChargeExpiration = raw?.surrender_charge_expiration;
  model.targetPremium = raw?.target_premium;
  model.taxType = raw?.tax_type;
  model.termRider = raw?.term_rider;
  model.tia = raw?.tia;
  model.totalPremium = raw?.premium?.total;
  model.trackingNumber = raw?.tracking_number;
  model.trackingNumberLink = raw?.tracking_number_link;
  model.trackingNumberType = raw?.tracking_number_type;
  model.updatedAt = raw?.updated_at;

  model.address = {
    title: raw?.address?.name,
    value: raw?.address?.id
  };
  model.assignOptions = (raw?.assign_options || []).filter(
    v => v.type !== "Vendor"
  ); //Remove once

  model.product = setCaseProductFromRequest(raw?.product);

  model.carrier = setCarrierFromRaw(raw?.carrier || raw?.product?.carrier);

  model.addresses = [];
  raw?.addresses?.forEach(address =>
    model.addresses.push({ title: address.text, value: address.value })
  );

  model.requirements = [];
  raw?.requirements?.forEach(req => {
    model.requirements.push(setCaseTaskPreviewFromRequest(req));
  });

  model.products = [];
  raw?.products?.forEach(rawProduct => {
    model.products.push(setCaseProductFromRequest(rawProduct));
  });

  model.appointmentCases = [];
  if (raw?.appointment_cases?.length) {
    raw.appointment_cases.forEach(c => {
      model.appointmentCases.push(setAppointmentCaseFromRequest(c));
    });
  }

  model.documents = raw?.documents?.map(setDocumentFromRequest);

  model.timeline = [];
  raw?.timeline?.forEach(rawTime => {
    model.timeline.push(setTimelineItemFromRequest(rawTime));
  });

  model.room = [];
  raw?.room?.forEach(rawAdvisor => {
    model.room.push(setCaseRoomAccessFromRequest(rawAdvisor));
  });

  model.caseManager = model.room.find(c => c.caseManager === true);
  model.caseConcierge = model.room.find(c => c.caseConcierge === true);

  model.notes = [];
  raw?.notes?.forEach(rawNote => {
    model.notes.push(setNoteFromRequest(rawNote));
  });
  model.notes.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  model.contractParties = [];
  raw?.contract_parties?.forEach(rawParty => {
    model.contractParties.push(setCaseContractPartyFromRequest(rawParty));
  });

  model.informals = [];
  raw?.informals?.forEach(rawInformal => {
    model.informals.push(setInformalOfferFromRequest(rawInformal));
  });

  model.quotes = [];
  if (raw?.quotes) {
    model.quotes = raw.quotes.map(setCaseQuoteFromRequest);
  }

  model.impairedRiskQuotes = [];
  if (raw?.quick_quotes) {
    model.impairedRiskQuotes = raw.quick_quotes.map(setCaseQuoteFromRequest);
  }

  model.conversionEapps = [];
  if (raw?.conversion_eapps) {
    model.conversionEapps = raw.conversion_eapps.map(setCaseEappFromRequest);
  }

  return model;
}

function CaseEapp(model = {}) {
  return {
    id: model?.id,
    createdAt: model?.createdAt
  };
}

function setCaseEappFromRequest(raw = {}) {
  const model = CaseEapp();
  model.id = raw?.id;
  model.createdAt = raw?.created_at;
  return model;
}

function CaseQuote(model = {}) {
  return {
    id: model?.id,
    status: model?.status,
    createdAt: model?.createdAt
  };
}

function setCaseQuoteFromRequest(raw = {}) {
  const model = CaseQuote();
  model.id = raw?.id;
  model.status = raw?.status;
  model.createdAt = raw?.created_at;
  return model;
}

function CaseProduct(model = {}) {
  return {
    id: model?.id,
    chronicIllnessRider: model?.chronicIllnessRider,
    ltcRider: model?.ltcRider,
    line: model?.line,
    name: model?.name,
    requireConversionExpirationDate: Boolean(
      model?.requireConversionExpirationDate
    ),
    requireTargetPremium: Boolean(model?.requireTargetPremium)
  };
}

function setCaseProductFromRequest(raw) {
  const model = CaseProduct();

  model.id = raw?.id;
  model.chronicIllnessRider = raw?.chronic_illness_rider;
  model.ltcRider = raw?.ltc_rider;
  model.termRider = raw?.term_rider;
  model.childRider = raw?.child_rider;
  model.line = raw?.line;
  model.name = raw?.name;
  model.requireConversionExpirationDate =
    raw?.require_conversion_expiration_date;
  model.requireTargetPremium = raw?.require_target_premium;
  return model;
}

export function TimelineItem(model = {}) {
  return {
    date: model?.date || null,
    description: model?.description || null,
    key: model?.key || null
  };
}

function setTimelineItemFromRequest(raw = {}) {
  const model = TimelineItem();
  model.date = raw?.date;
  model.description = raw?.description;
  model.key = raw?.key;
  return model;
}

export function CaseRevenue(model = {}) {
  return {
    statementId: model?.statementId,
    payPeriodId: model?.payPeriodId,
    paidTo: model?.paidTo,
    paymentType: model?.paymentType,
    payor: model?.payor,
    type: model?.type,
    statementDate: model?.statementDate,
    split: model?.split,
    premium: model?.premium,
    commissionPercent: model?.commissionPercent,
    commission: model?.commission,
    key: model?.key
  };
}

export function setCaseRevenueFromRequest(rawModel = {}) {
  const caseRevenue = CaseRevenue();
  caseRevenue.payPeriodId = rawModel?.pay_period_id;
  caseRevenue.paidTo = rawModel?.paid_to;
  caseRevenue.payor = rawModel?.payor;
  caseRevenue.type = rawModel?.type;
  caseRevenue.paymentType = rawModel?.payment_type;
  caseRevenue.statementDate = rawModel?.statement_date;
  caseRevenue.split = rawModel?.split;
  caseRevenue.premium = rawModel?.premium;
  caseRevenue.commissionPercent = rawModel?.commission_percent;
  caseRevenue.commission = rawModel?.commission;
  caseRevenue.statementId = rawModel?.statement_id;
  caseRevenue.key = uuidv4();
  return caseRevenue;
}

export function CaseTransaction(model = {}) {
  return {
    transactionId: model?.transactionId,
    commissionType: model?.commissionType,
    totalPoints: model?.totalPoints,
    amount: model?.amount,
    statementDate: model?.statementDate,
    createdAt: model?.createdAt,
    override: model?.override,
    directCommission: model?.directCommission,
    statementId: model?.statementId,
    createdBy: model?.createdBy,
    memo: model?.memo
  };
}

export function setCaseTransactionFromRequest(rawModel = {}) {
  const caseTransaction = CaseTransaction();

  caseTransaction.id = rawModel?.id;
  caseTransaction.commissionType = rawModel?.commission_type;
  caseTransaction.totalPoints = rawModel?.total_points;
  caseTransaction.amount = rawModel?.amount;
  caseTransaction.statementDate = rawModel?.statement_date;
  caseTransaction.createdAt = rawModel?.created_at;
  caseTransaction.override = rawModel?.override;
  caseTransaction.directCommission = rawModel?.direct_commission;
  caseTransaction.statementId = rawModel?.statement_id;
  caseTransaction.createdBy = rawModel?.created_by;
  caseTransaction.memo = rawModel?.memo;
  caseTransaction.key = uuidv4();

  return caseTransaction;
}

export function AppointmentCase(model = {}) {
  return {
    advisorId: model?.advisorId || 0,
    advisorName: model?.advisorName || "",
    advisorPhone: model?.advisorPhone || "",
    advisorProfileLink: model?.advisorProfileLink || null,
    advisorSettingsLink: model?.advisorSettingsLink || null,
    advisorType: model?.advisorType || "",
    appointmentAssignmentRouterLink:
      model?.appointmentAssignmentRouterLink || null,
    appointmentCaseId: model?.appointmentCaseId || 0,
    appointmentCode: model?.appointmentCode || "",
    appointmentId: model.appointmentId || 0,
    appointmentRouterLink: model?.appointmentRouterLink || null,
    assignmentId: model?.assignmentId || 0,
    assignmentName: model?.assignmentName || "",
    backNineAppointment: Boolean(model?.backNineAppointment),
    carrierAgreementType: model?.carrierAgreementType,
    caseId: model?.caseId || 0,
    estimatedAgentFirstYearAndExcessCommission:
      model?.estimatedAgentFirstYearAndExcessCommission,
    insuredName: model?.insuredName || "",
    marketingManagerId: model?.marketingManagerId || 0,
    marketingManagerName: model?.marketingManagerName || "",
    splitPercent: model?.splitPercent || null,
    splitRenewal: model?.splitRenewal || null
  };
}

export function setAppointmentCaseFromRequest(model = {}) {
  const apptCase = AppointmentCase();

  apptCase.advisorId = model?.advisor?.id;
  apptCase.advisorName = model?.advisor?.name;
  apptCase.advisorPhone = model?.advisor?.phone_work;
  apptCase.advisorType = model?.advisor?.type?.model;
  apptCase.appointmentCaseId = model.id;
  apptCase.appointmentCode = model?.appointment?.name;
  apptCase.appointmentId = model?.appointment?.id;
  apptCase.assignmentId = model?.appointment?.assignment?.id;
  apptCase.assignmentName = model?.appointment?.assignment?.name;
  apptCase.backNineAppointment = model?.commissionable;
  apptCase.carrierAgreementType = model?.appointment?.carrier_agreement_type;
  apptCase.estimatedAgentFirstYearAndExcessCommission =
    model?.estimated_agent_first_year_and_excess_commission;
  apptCase.insuredName = model?.insured?.name;
  apptCase.marketingManagerId = model?.marketing_manager?.id;
  apptCase.marketingManagerName = model?.marketing_manager?.name;
  apptCase.splitPercent = Math.trunc((model?.split_percent || 0) * 100);
  apptCase.splitRenewal = Math.trunc((model?.renewal_split || 0) * 100);

  if (apptCase.advisorType) {
    const isAgency = apptCase.advisorType === "Agency";
    apptCase.ssnOrTin = isAgency ? "tin" : "ssn";
    apptCase.advisorProfileLink = getRouterLink(
      apptCase.advisorType,
      apptCase.advisorId
    );
    apptCase.advisorSettingsLink = getRouterLink(
      `${apptCase.advisorType}Settings`,
      apptCase.advisorId
    );
  }

  if (apptCase.appointmentId) {
    apptCase.appointmentRouterLink = getRouterLink(
      "Appointment",
      apptCase.appointmentId
    );
  }

  if (apptCase.assignmentId) {
    apptCase.appointmentAssignmentRouterLink = getRouterLink(
      "Appointment",
      apptCase.assignmentId
    );
  }

  return apptCase;
}

export function setRequestFromAppointmentCase(appointmentCase) {
  const ac = AppointmentCase(appointmentCase);
  return {
    appointment_id: ac.appointmentCode,
    case_id: ac.caseId,
    split_percent: (Number(ac.splitPercent) / 100).toFixed(4),
    renewal_split: (Number(ac.splitRenewal) / 100).toFixed(4)
  };
}

export function setUpdateRequestFromAppointmentCase(appointmentCase) {
  const ac = AppointmentCase(appointmentCase);
  return {
    split_percent: ac.splitPercent / 100,
    renewal_split: ac.splitRenewal / 100,
    marketing_manager_id: ac.marketingManagerId,
    commissionable: Boolean(ac.backNineAppointment)
  };
}

function CaseRoomAccess(model = {}) {
  return {
    advisorAccess: model?.advisorAccess || null,
    agent: model?.agent || null,
    avatar: model?.avatar || null,
    canBeCaseManager: model?.canBeCaseManager || null,
    carrierAccess: model?.carrierAccess || null,
    carrierEmail: model?.carrierEmail || null,
    caseAccessId: model?.caseAccessId || null,
    caseConcierge: model?.caseConcierge || null,
    caseManager: model?.caseManager || null,
    disabled: model?.disabled || null,
    email: model?.email || null,
    id: model?.id || null,
    link: model?.link || null,
    name: model?.name || null,
    personnelDepartment: model?.personnelDepartment || null,
    phone: model?.phone || null,
    routerLink: model?.routerLink || null,
    type: model?.type || null,
    userEmail: model?.userEmail || null
  };
}

export function setCaseRoomAccessFromRequest(raw = {}) {
  const model = CaseRoomAccess();
  model.additionalCaseAccess = raw?.additional_case_access;
  model.advisorAccess = raw?.advisor_access;
  model.agent = raw?.agent;
  model.avatar = raw?.avatar_url;
  model.canBeCaseManager = raw?.can_be_case_manager;
  model.carrierAccess = raw?.carrier_access;
  model.carrierEmail = raw?.carrier_email;
  model.caseAccessId = raw?.case_access_id;
  model.caseConcierge = raw?.case_concierge;
  model.caseManager = raw?.case_manager;
  model.disabled = raw?.disabled;
  model.email = raw?.email;
  model.id = raw?.id;
  model.link = raw?.link;
  model.name = raw?.name;
  model.personnelDepartment = raw?.personnel_department;
  model.phone = raw?.phone;
  model.type = raw?.type;
  model.userEmail = raw?.advisor_email;

  if (model.type && model.id) {
    model.routerLink = getRouterLink(model.type, model.id);
  }

  return model;
}

export function CaseTask(model = {}) {
  return {
    assigned: {
      id: model?.assigned?.id || null,
      name: model?.assigned?.name || null,
      type: model?.assigned?.type || null
    },
    createdAt: model?.createdAt || null,
    completedAt: model?.completedAt || null,
    description: model?.description || null,
    documents: model?.documents || [],
    email: Boolean(model?.email),
    followUpDate: model?.followUpDate || null,
    hidden: Boolean(model?.hidden),
    id: model?.id || null,
    notes: model?.notes || [],
    orderId: model?.orderId || null,
    orderNumber: model?.orderNumber || null,
    ownableName: model?.ownableName || null,
    status: model?.status || null,
    type: model?.type || null,
    updatedAt: model?.updatedAt || null,
    vendor: model?.vendor || null
  };
}
export const CASE_TASK_TYPE = {
  TASK: "Task",
  EXAM: "Exam",
  PHYSICIAN_STATEMENT: "PhysicianStatement"
};

export const CASE_TASK_STATUS = {
  COMPLETE: "Completed",
  INCOMPLETE: "Outstanding"
};

export function setCaseTaskFromTask(t = {}, type) {
  let task;
  let includeOrderDetails = false;
  if (type === CASE_TASK_TYPE.TASK) {
    task = Task(t);
  } else if (type === CASE_TASK_TYPE.EXAM) {
    task = Exam(t);
    includeOrderDetails = true;
  } else if (type === CASE_TASK_TYPE.PHYSICIAN_STATEMENT) {
    task = PhysicianStatement(t);
    includeOrderDetails = true;
  } else {
    throw new Error("Invalid task type");
  }
  const caseTask = CaseTask();
  caseTask.assigned = {
    id: task.assigned.id,
    name: task.assigned.name,
    type: task.assigned.type
  };

  caseTask.createdAt = task.createdAt;
  caseTask.completedAt = task.completedAt;
  caseTask.description = task.description;
  caseTask.documents = task.documents;
  caseTask.email = task.email;
  caseTask.followUpDate = task.followUpDate;
  caseTask.hidden = task.hidden;
  caseTask.id = task.id;
  caseTask.notes = task.notes;
  caseTask.notes.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  caseTask.ownableName = task.ownable.name;
  caseTask.status = task.status;
  caseTask.type = type;
  caseTask.updatedAt = task.updatedAt;

  if (includeOrderDetails) {
    caseTask.orderId = task.orderId;
    caseTask.orderNumber = task.orderNumber;
    caseTask.vendor = task.vendor;
  }

  return caseTask;
}

function CaseTaskPreview(model = {}) {
  return {
    status: model?.status,
    completedAt: model?.completedAt,
    createdAt: model?.createdAt,
    description: model?.description,
    followUp: model?.followUp,
    updatedAt: model?.updatedAt,
    email: model?.email,
    id: model?.id,
    type: model?.type,
    hidden: model?.hidden,
    key: model?.key
  };
}

function getCaseTaskPreviewKey(casePreview) {
  return `${casePreview.type}-${casePreview.id}`;
}

export function setCaseTaskPreviewFromRequest(raw = {}) {
  const model = CaseTaskPreview();
  model.status = raw?.status;
  model.email = Boolean(raw?.email);
  model.description = raw?.description;
  model.id = raw?.id;
  model.type = raw?.type;
  model.hidden = raw?.hidden;
  model.key = getCaseTaskPreviewKey(model);

  if (raw?.dates?.completed_at) {
    model.completedAt = new Date(raw.dates.completed_at);
  }
  if (raw?.dates?.created_at) {
    model.createdAt = new Date(raw.dates.created_at);
  }
  if (raw?.dates?.updated_at) {
    model.updatedAt = new Date(raw.dates.updated_at);
  }
  if (raw?.dates?.follow_up_date) {
    model.followUp = new Date(raw.dates.follow_up_date);
  }

  return model;
}

export function setCaseTaskPreviewFromCaseTask(caseTask = CaseTask()) {
  const model = CaseTaskPreview();
  model.status = caseTask.status;
  model.description = caseTask.description;
  model.id = caseTask.id;
  model.type = caseTask.type;
  model.hidden = caseTask.hidden;
  model.email = caseTask.email;
  model.key = getCaseTaskPreviewKey(model);

  if (caseTask.completedAt) {
    model.completedAt = new Date(caseTask.completedAt);
  }
  if (caseTask.followUpDate) {
    model.followUp = new Date(caseTask.followUpDate);
  }
  if (caseTask.updatedAt) {
    model.updatedAt = new Date(caseTask.updatedAt);
  }
  if (caseTask.createdAt) {
    model.createdAt = new Date(caseTask.createdAt);
  }
  return model;
}

export function CaseSearchItem(model = {}) {
  return {
    id: model.id,
    line: model.line || null,
    policyNumber: model.policyNumber || null,
    carrier: CaseSearchItemCarrier(model?.carrier),
    appointmentCases: model.appointmentCases || []
  };
}

export function setCaseSearchItemFromRequest(raw = {}) {
  const model = CaseSearchItem();
  model.id = raw?.id;
  model.line = raw?.line;
  model.policyNumber = raw?.policy_number;
  model.appointmentCases = raw?.appointment_cases.map(
    setCaseSearchItemAppointmentCaseFromRequest
  );
  model.carrier = setCaseSearchItemCarrierFromRequest(raw?.carrier);

  return model;
}

function CaseSearchItemAppointmentCase(model = {}) {
  return {
    owner: CaseSearchItemAppointmentCaseOwner(model?.owner),
    marketingManager: CaseSearchItemAppointmentCaseMarketingManager(
      model?.marketingManager
    ),
    id: model?.id,
    splitPercent: model?.splitPercent
  };
}

function setCaseSearchItemAppointmentCaseFromRequest(raw = {}) {
  const model = CaseSearchItemAppointmentCase();
  model.owner = setCaseSearchItemAppointmentCaseOwnerFromRequest(raw?.owner);
  model.marketingManager = setCaseSearchItemAppointmentCaseMarketingManager(
    raw?.marketing_manager
  );
  model.id = raw?.id;
  model.splitPercent = raw?.split_percent;
  return model;
}

function CaseSearchItemAppointmentCaseMarketingManager(model = {}) {
  return {
    name: model?.name
  };
}

function setCaseSearchItemAppointmentCaseMarketingManager(raw = {}) {
  const model = CaseSearchItemAppointmentCaseMarketingManager();
  model.name = raw?.name;
  return model;
}

function CaseSearchItemAppointmentCaseOwner(model = {}) {
  return {
    avatarUrl: model?.avatarUrl,
    email: model?.email,
    id: model?.id,
    name: model?.name,
    npn: model?.npn,
    phoneWork: model?.phoneWork,
    title: model?.title,
    type: model?.type
  };
}

function setCaseSearchItemAppointmentCaseOwnerFromRequest(raw = {}) {
  const model = CaseSearchItemAppointmentCaseOwner();
  model.avatarUrl = raw?.avatar_url;
  model.email = raw?.email;
  model.id = raw?.id;
  model.name = raw?.name;
  model.npn = raw?.npn;
  model.phoneWork = raw?.phone_work;
  model.title = raw?.title;
  model.type = raw?.type?.model;
  return model;
}

function CaseSearchItemCarrier(model = {}) {
  return {
    id: model?.id,
    name: model?.name,
    payorId: model?.payorId
  };
}

function setCaseSearchItemCarrierFromRequest(raw = {}) {
  const model = CaseSearchItemCarrier();
  model.id = raw?.id;
  model.name = raw?.name;
  model.payorId = raw?.payor_id;
  return model;
}

export function CaseTotal(model = {}) {
  return {
    payableName: model?.payableName,
    amount: model?.amount,
    paymentTypes: model?.paymentTypes || [],
    types: model?.types
  };
}

export function setCaseTotalFromRequest(raw = {}) {
  const model = CaseTotal();
  model.payableName = raw?.payable?.name;
  model.amount = raw?.amount;
  model.paymentTypes = raw?.payment_types || [];

  model.types = model.paymentTypes?.join(", ");
  return model;
}

export function CaseCommission(model) {
  return {
    assignedPayableName: model?.assignedPayableName,
    commissionType: model?.commissionType,
    endYear: model?.endYear,
    payableName: model?.payableName,
    paymentType: model?.paymentType,
    payorName: model?.payorName,
    percent: model?.percent,
    startYear: model?.startYear,
    createdBy: model?.createdBy,
    createdAt: model?.createdAt,
    years: model?.years
  };
}

export function setCaseCommissionFromRequest(raw = {}) {
  const model = CaseCommission();
  model.assignedPayableName = raw?.assigned_payable?.name;
  model.commissionType = raw?.commission_type;
  model.endYear = raw?.end_year;
  model.payableName = raw?.payable?.name;
  model.paymentType = raw?.payment_type;
  model.payorName = raw?.payor?.name;
  model.percent = raw?.percent;
  model.startYear = raw?.start_year;
  model.createdBy = raw?.created_by;
  model.createdAt = raw?.created_at;
  model.years = `${model.startYear} - ${model.endYear}`;
  return model;
}

export function Carrier(model = {}) {
  return {
    avatarUrl: model?.avatarUrl || null,
    id: model?.id || undefined,
    name: model?.name || undefined,
    products: model?.products || [],
    phone: model?.phone || {},
    supportsInstantAps: Boolean(model?.supportsInstantAps)
  };
}

export function setCarrierFromRaw(rawModel = {}) {
  const carrier = Carrier();
  carrier.avatarUrl = rawModel?.avatar_url;
  carrier.id = rawModel?.id;
  carrier.name = rawModel?.name;
  carrier.products = rawModel?.products;
  carrier.phone = rawModel?.phone;
  carrier.supportsInstantAps = rawModel?.instant_aps;
  return carrier;
}

export function ContractParty(model = {}) {
  return {
    contractParty: model?.contractParty || null,
    avatar: model?.avatar || null,
    birthdate: model?.birthdate || null,
    name: model?.name || null,
    partyId: model?.partyId || null,
    roles: model?.roles || [],
    type: model?.type || null,
    addresses: model?.addresses || {},
    gender: model?.gender || ""
  };
}

export function setCaseContractPartyFromRequest(raw = {}) {
  const model = ContractParty();
  model.avatar = raw?.avatar_url;
  model.birthdate = raw?.birthdate;
  model.id = raw?.id;
  model.name = raw?.name;
  model.partyId = raw?.party_id;
  model.email = raw?.email;
  model.gender = raw?.gender;
  model.primaryPhone = raw?.primary_phone;

  model.type = {
    individual: "Individual",
    entity: "Entity"
  }[raw?.type];

  model.roles = [];
  raw?.roles?.forEach(rawRole => {
    model.roles.push(setRoleFromRequest(rawRole));
  });

  if (raw?.address) {
    model.addresses = [setAddressFromRaw(raw?.address)];
  }
  return model;
}
